import { motion } from "framer-motion";
import { TrendingUp, Star as StarIcon, Zap, Users, Target, BarChart3 } from "lucide-react";
import { useAIAgentData } from "../../hooks/useAIAgentData";

// Icon mapping dictionary to resolve string names to Lucide icon components
const iconMap: Record<string, React.ComponentType<any>> = {
  TrendingUp,
  Star: StarIcon,
  Zap,
  Users,
  Target,
  BarChart3,
};

function renderIcon(name: string, size: number, className?: string) {
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} className={className} /> : null;
}

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

export default function OutcomesSection() {
  const { data } = useAIAgentData();
  const { badgeText, title, outcomes } = data.outcomesSection;

  return (
    <section className="relative px-5 sm:px-10 md:px-16 sm:py-16 py-8">
      <div className="space-y-16">
        <div className="text-center space-y-4">
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-[#A6A0F6] text-sm font-medium tracking-widest uppercase">
            {badgeText}
          </motion.p>
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="text-4xl md:text-6xl font-bold text-white">
            {title}
          </motion.h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {outcomes.map((o, i) => (
            <motion.div
              key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              className="flex gap-5 p-6 rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[#1179F9]/30 transition-colors"
            >
              <div className="p-3 rounded-xl bg-[#1179F9]/15 text-[#1179F9] h-fit flex-shrink-0">{renderIcon(o.iconName, 24)}</div>
              <div className="space-y-2">
                <h4 className="text-xl text-white font-semibold">{o.title}</h4>
                <p className="text-lg text-[#d9d9d9] leading-relaxed">{o.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
